// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Brim — the menu / help / about / results markup. Attribution backlink is mandatory
// (it's how players find the rest of the catalogue) and licence copy never appears here.

import { escapeHtml, fmtScore } from './dom';
import { MODE_IDS, modeOf, type Mode, type ModeId } from './modes';
import type { OrbResult } from './game';
import type { PeerResult } from './race';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
  '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

function modeChips(active: ModeId): string {
  return MODE_IDS.map(
    (id) =>
      `<button class="chip${id === active ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === active}">${escapeHtml(
        modeOf(id).name,
      )}</button>`,
  ).join('');
}

export function menuHtml(o: { modeId: ModeId; best: number; muted: boolean; dateStr: string }): string {
  const mode = modeOf(o.modeId);
  return `
  <section class="screen menu">
    <header class="brand">
      <div class="brand-orb" aria-hidden="true"><span></span></div>
      <h1>Brim</h1>
      <p class="tag">Hold the light steady. Let go before it overflows.</p>
    </header>

    <div class="modes" role="group" aria-label="Choose a mode">${modeChips(o.modeId)}</div>
    <p class="mode-blurb">${escapeHtml(mode.blurb)}</p>

    <div class="menu-actions">
      <button class="btn primary" id="playSolo">Play</button>
      <button class="btn" id="playDaily">Daily orbs · ${escapeHtml(o.dateStr)}</button>
      <button class="btn" id="playFriends">Play with friends</button>
    </div>
    <p class="best-line">${o.best > 0 ? `Best in ${escapeHtml(mode.name)}: <strong>${fmtScore(o.best)}</strong>` : '&nbsp;'}</p>

    <div class="menu-links">
      <button class="linkbtn" id="help">How to play</button>
      <button class="linkbtn" id="about">About</button>
      <button class="linkbtn" id="mute">${o.muted ? 'Sound: off' : 'Sound: on'}</button>
    </div>
  </section>`;
}

export function helpHtml(): string {
  return `
  <button class="sheet-close" aria-label="Close">✕</button>
  <h2>How to play</h2>
  <ol class="help-list">
    <li>An orb of light <strong>swells</strong> in your hands — the fuller it gets, the more it's worth.</li>
    <li>But it has a <strong>hidden brim</strong>: hold too long and it <strong>overflows</strong>, and you lose it all.</li>
    <li>Hit <strong>Release</strong> to bank the charge before that happens. Watch the ring close in and redden — that's your warning.</li>
    <li><strong>On a phone: don't flinch.</strong> A jolt to your steady hand can <strong>spill</strong> the orb. Hold the phone dead still.</li>
    <li>Bank more than your friends. No motion sensor? Just hold your nerve and release before it overflows.</li>
  </ol>
  <p class="help-controls"><strong>Desktop:</strong> press <kbd>Space</kbd> (or the Release button) to bank · <kbd>P</kbd> pause · <kbd>R</kbd> restart.</p>`;
}

export function aboutHtml(): string {
  return `
  <button class="sheet-close" aria-label="Close">✕</button>
  <h2>About Brim</h2>
  <p>A hold-your-nerve party game. On a phone the motion sensor measures how still you hold it — nothing is
     recorded, stored, or sent anywhere; the game only ever uses a single number for how much your hand moved.
     With no sensor it plays as a pure timing gamble, which is a real game in its own right.</p>
  <p>Play solo, chase the daily orbs, or share a room link and race friends on the identical orbs — peer-to-peer,
     with no game server. Connecting to friends uses a public signaling relay only to broker the direct link; no
     gameplay is stored anywhere.</p>
  <p>${ATTRIB}</p>`;
}

function orbRow(r: OrbResult): string {
  if (r.bust) {
    const label = r.bustKind === 'overflow' ? 'overflowed' : 'spilled';
    return `<li class="orb-row bust"><span class="orb-n">${r.index + 1}</span>
      <span class="orb-out">${label}</span>
      <span class="orb-val">0 <em>/ ${fmtScore(r.potCeil)}</em></span></li>`;
  }
  return `<li class="orb-row"><span class="orb-n">${r.index + 1}</span>
    <span class="orb-out">banked${r.mult > 1 ? ` ×${r.mult.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}` : ''}</span>
    <span class="orb-val">${fmtScore(r.banked)} <em>/ ${fmtScore(r.potCeil)}</em></span></li>`;
}

export function soloResultsHtml(o: {
  mode: Mode;
  score: number;
  best: number;
  isNewBest: boolean;
  daily: boolean;
  results: OrbResult[];
}): string {
  const banked = o.results.filter((r) => !r.bust).length;
  return `
  <section class="screen results">
    <h2 class="res-title">${o.isNewBest ? 'New best!' : 'Run over'}</h2>
    <p class="res-score">${fmtScore(o.score)}<span class="res-score-cap">charge banked</span></p>
    <p class="res-sub">${escapeHtml(o.mode.name)}${o.daily ? ' · daily' : ''} · banked ${banked} of ${o.results.length} orbs · best ${fmtScore(o.best)}</p>
    <ul class="orb-list">${o.results.map(orbRow).join('')}</ul>
    <p class="res-note">The faint number is the most that orb could ever have held — its brim.</p>
  </section>`;
}

export function multiResultsHtml(ranked: PeerResult[], selfSeat: number | null, mode: Mode, tally: number[]): string {
  const top = ranked.find((r) => !r.left);
  const rows = ranked
    .map((r, i) => {
      const me = r.seat === selfSeat;
      const lead = r.left ? '—' : r.finished ? `${i + 1}` : '…';
      const wins = tally[r.seat] ?? 0;
      const isWinner = top && r.seat === top.seat && r.finished && !r.left;
      return `<li class="mp-row${me ? ' me' : ''}${r.left ? ' left' : ''}">
        <span class="mp-rank">${lead}</span>
        <span class="mp-name">${escapeHtml(r.name)}${me ? ' <em>(you)</em>' : ''}${isWinner ? ' <span class="mp-star" title="Most banked">★</span>' : ''}</span>
        <span class="mp-score">${r.left ? 'left' : fmtScore(r.score)}</span>
        <span class="mp-wins">${wins > 0 ? `${wins}★` : ''}</span>
      </li>`;
    })
    .join('');
  return `
    <h2 class="res-title">Results</h2>
    <p class="res-sub">${escapeHtml(mode.name)} · most charge banked wins</p>
    <ul class="mp-list">${rows}</ul>`;
}
